import { useQuery } from "@tanstack/react-query";

import {
	fetchManualTree,
	searchPatientManuals,
	type ManualTree,
} from "@/api/adapters/patients";

/**
 * Hook: the consultation-manual tree (영역 > 하위영역 > 항목).
 *
 * Reference data owned by the admin app — it changes when an administrator publishes a manual, not while a
 * clinician is reading — so it is cached for the session rather than refetched on every open.
 */
export function useManualTree() {
	return useQuery<ManualTree>({
		queryKey: ["consultation-manual", "tree"],
		queryFn: fetchManualTree,
		staleTime: Infinity,
	});
}

/**
 * Hook: search the manuals.
 *
 * Returns the same TREE, filtered — the pane keeps its 영역/하위영역 structure while searching instead of
 * collapsing into a flat list. `null` means "no search is active", and the caller renders the full tree; an
 * empty array means "searched, nothing matched", and the caller renders the empty state. The two are different
 * states and must not be collapsed into one.
 */
export function useManualSearch(query: string): ManualTree | null {
	const term = query.trim();
	const active = term.length > 0;
	const { data } = useQuery<ManualTree>({
		queryKey: ["consultation-manual", "search", term],
		queryFn: () => searchPatientManuals(term),
		enabled: active,
	});
	return active ? (data ?? null) : null;
}
